import * as errors from './errors/index.js';
import ActiveRecordPagination from './ActiveRecordPagination.js';
/**
 * @private
 * @param {function[]} requests
 * @param {ActiveRecord[]} records
 * @return {Promise<ActiveRecord[]>}
 */
async function sequentially(pagination, pageNumbers, initial) {
    const accumulated = [...initial];
    let chain = Promise.resolve();
    pageNumbers.forEach((pageNumber) => {
        chain = chain
            .then(() => pagination.page(pageNumber))
            .then((records) => {
            accumulated.push(...records);
        });
    });
    return chain.then(() => accumulated);
}
/**
 * @private
 */
function single({ records }) {
    return Array.isArray(records) ? records[0] : records;
}
/**
 * @private
 */
function list({ records }) {
    return Array.isArray(records) ? records : [];
}
/**
 * @memberof activerecord
 * @classdesc
 * Core interface for translating ActiveRecord methods into requests made to the
 * "external" sources. Responsible for translation (encoding / decoding) as well
 * as managing basic "defaults" applied to every request.
 *
 * ActiveRecordSource can be subclassed. Changes can also be made at the
 * ActiveRecordSource level which impacts default behavior of all consumers
 * for things like building an HTTP request, static declaration of headers, etc.
 */
class ActiveRecordSource {
    /**
     * @param {string} url
     * @param {SourceOptions} opts
     */
    constructor(url, opts = {}) {
        this.url = url;
        // Important property names
        this.primaryKeyPropertyName = 'primaryKeyPropertyName' in opts ? opts.primaryKeyPropertyName : 'id';
        this.responseRecordsPropertyName = 'responseRecordsPropertyName' in opts ? opts.responseRecordsPropertyName : 'data';
        this.responseMetadataPropertyName = 'responseMetadataPropertyName' in opts ? opts.responseMetadataPropertyName : 'meta';
        // Boolean options
        this.useTrailingSlash = !!opts.useTrailingSlash;
        // TODO: Support disabling "pagination" related methods
        // Pull defaults from the class level so sub-classes can redclare defaults
        const Source = this.constructor;
        // Provided functions
        this.log = opts.log || Source.log;
        this.request = opts.request || Source.request;
        this.headers = opts.headers || {};
        // Transformers
        this.transformQueryString = opts.transformQueryString || this.transformQueryString;
        this.transformRequest = opts.transformRequest || this.transformRequest;
        this.transformRequestBody = opts.transformRequestBody || this.transformRequestBody;
        this.transformRequestHeaders = opts.transformRequestHeaders || this.transformRequestHeaders;
        this.transformResponse = opts.transformResponse || this.transformResponse;
        this.transformResponseRecords = (opts.transformResponseRecords || this.transformResponseRecords);
        this.transformResponseMetadata = (opts.transformResponseMetadata || this.transformResponseMetadata);
    }
    /**
     * @param {object} props
     * @return {?(string|number)}
     */
    findPrimaryKey(props) {
        return props[this.primaryKeyPropertyName] || null;
    }
    /**
     * @param {object} opts
     * @param {string} [opts.primaryKey]
     * @param {object} [opts.params]
     * @return {string}
     */
    urlFor({ primaryKey, params }) {
        const primaryKeyPath = primaryKey ? `/${primaryKey}` : '';
        const trailingSlash = this.useTrailingSlash ? '/' : '';
        return this.url + primaryKeyPath + trailingSlash + this.transformQueryString(params);
    }
    /**
     * @param {ActiveRecord} props
     * @param {object} [params]
     * @param {object} [opts]
     * @return {Promise<object>}
     */
    create(props, params = {}, opts = {}) {
        // const primaryKey = this.findPrimaryKey(props);
        // const name = this.constructor.name;
        // if (primaryKey) throw new Error(`"${name}#create" does not expect a value at "${this.primaryKeyPropertyName}"`);
        return this.handleRequest(this.urlFor({ params }), {
            method: 'POST',
            body: this.transformRequestBody(props),
        }, opts)
            .then(({ records }) => single({ records }));
    }
    /**
     * @param {number|string} primaryKey
     * @param {object} [params]
     * @param {object} [opts]
     * @return {Promise<object>}
     */
    retrieve(primaryKey, params = {}, opts = {}) {
        const name = this.constructor.name;
        if (!primaryKey)
            throw new Error(`"${name}#retrieve" requires a primary key`);
        return this.handleRequest(this.urlFor({ params, primaryKey }), { method: 'GET' }, opts)
            .then(({ records }) => single({ records }));
    }
    /**
     * @param {ActiveRecord} props
     * @param {object} [params]
     * @param {object} [opts]
     * @return {Promise<object>}
     */
    update(props, params = {}, opts = {}) {
        const primaryKey = this.findPrimaryKey(props);
        const name = this.constructor.name;
        if (!primaryKey)
            throw new Error(`"${name}#update" requires a value to exist at "${this.primaryKeyPropertyName}"`);
        return this.handleRequest(this.urlFor({ params, primaryKey }), {
            method: 'PUT',
            body: this.transformRequestBody(props),
        }, opts)
            .then(({ records }) => single({ records }));
    }
    /**
     * @param {number|string} primaryKey
     * @param {object} [params]
     * @param {object} [opts]
     * @return {Promise<object>}
     */
    delete(primaryKey, params = {}, opts = {}) {
        const name = this.constructor.name;
        if (!primaryKey)
            throw new Error(`"${name}#delete" requires a primary key'`);
        return this.handleRequest(this.urlFor({ params, primaryKey }), { method: 'DELETE' }, opts)
            .then(({ records }) => single({ records }));
    }
    /**
     * @param {object} [params]
     * @param {object} [opts]
     * @return {Promise<object[]>}
     */
    where(params = {}, opts = {}) {
        return this.handleRequest(this.urlFor({ params }), { method: 'GET' }, opts)
            .then(({ records, meta }) => {
            const metadata = meta;
            const pageCount = metadata ? metadata.totalPages : null;
            if (ActiveRecordSource.debug && !Number.isInteger(params.page) && pageCount > 1) {
                const name = this.constructor.name;
                this.log(`"${name}#where" was called without a "page" parameter, BUT the source responded with ${pageCount} pages.`);
            }
            return { records, meta };
        })
            .then(list);
    }
    /**
     * @param {object} RecordClass
     * @param {object} [params]
     * @param {object} [opts]
     * @return {ActiveRecordPagination}
     */
    paginate(RecordClass, params = {}, opts = {}) {
        const Source = this.constructor;
        return new Source.Pagination(RecordClass, this, params, opts);
    }
    /**
     * @param {number} pageNumber
     * @param {object} [params]
     * @param {object} [opts]
     * @return {Promise<object>}
     */
    page(pageNumber, params = {}, opts = {}) {
        const page = { page: pageNumber };
        const parameters = { ...params, ...page };
        return this.handleRequest(this.urlFor({ params: parameters }), { method: 'GET' }, opts);
    }
    /**
     * @param {object} RecordClass
     * @param {object} [params]
     * @param {object} [opts]
     * @return {Promise<object[]>}
     */
    all(RecordClass, params = {}, opts = {}) {
        const pagination = this.paginate(RecordClass, params, opts);
        // Load first page
        return pagination.first()
            .then((records) => {
            // List of remaining pages w/o page 1
            const { pageNumbers } = pagination;
            pageNumbers.shift();
            // Sequentially load each page number; concat results
            return sequentially(pagination, pageNumbers, records);
        });
    }
    /**
     * @param {string} url
     * @param {object} request
     * @param {object} opts
     * @return {Promise<object>}
     */
    handleRequest(url, request, opts) {
        if (!url)
            throw new Error('"ActiveRecordSource#handleRequest" requires a URL to make a request');
        const headers = { headers: this.transformRequestHeaders(opts.headers) };
        const requestWithOpts = {
            ...request,
            ...opts,
            ...headers,
        };
        return this.request(url, this.transformRequest(requestWithOpts))
            .then((response) => this.handleResponse(response));
    }
    /**
     * @param {object} response
     * @return {Promise<object>}
     */
    handleResponse(response) {
        return this.transformResponse(response)
            .then((body) => {
            if (response.status < 400) {
                return {
                    response,
                    body,
                    records: this.transformResponseRecords(body),
                    meta: this.transformResponseMetadata(body),
                };
            }
            else if (response.status > 499) {
                throw new errors.ServerError({ response, body });
            }
            else if (response.status === 400) {
                throw new errors.ValidationError({ response, body });
            }
            else if (response.status === 401) {
                throw new errors.UnauthorizedError({ response, body });
            }
            else if (response.status === 403) {
                throw new errors.PermissionDeniedError({ response, body });
            }
            else if (response.status === 404) {
                throw new errors.NotFoundError({ response, body });
            }
            else {
                throw new errors.ActiveRecordError({ response, body });
            }
        });
    }
    /**
     * @param {object} request
     * @return {object}
     */
    transformRequest(request) {
        return request;
    }
    /**
     * @param {object} props
     * @return {object}
     */
    transformRequestBody(props) {
        // Make a shallow copy
        const copy = { ...props };
        // Remove properties implemented by ActiveRecord
        delete copy.$status;
        return JSON.stringify(copy);
    }
    /**
     * @param {object} transformRequestHeaders
     * @return {object}
     */
    transformRequestHeaders(custom) {
        const Source = this.constructor;
        return { ...Source.defaults.headers, ...this.headers, ...custom };
    }
    /**
     * @param {object} response
     * @return {Promise<object>}
     */
    transformResponse(response) {
        return (response.headers.get('Content-Type') === 'application/json'
            ? response.json()
            : Promise.resolve(response));
    }
    /**
     * @param {object} response
     * @return {object[]}
     */
    transformResponseRecords(responseBody) {
        return (this.responseRecordsPropertyName && responseBody
            ? responseBody[this.responseRecordsPropertyName]
            : responseBody);
    }
    /**
     * @param {object} response
     * @return {object}
     */
    transformResponseMetadata(responseBody) {
        // TODO: Enforce an interface for response especially for pagination properties
        return (this.responseMetadataPropertyName && responseBody
            ? responseBody[this.responseMetadataPropertyName]
            : responseBody);
    }
    /**
     * @param {object} [fromParameters]
     * @return {string}
     */
    transformQueryString(fromParameters = {}) {
        const params = new URLSearchParams();
        Object.keys(fromParameters || {}).forEach((key) => {
            const value = fromParameters[key];
            if (Array.isArray(value)) {
                value.forEach((entry) => params.append(key, entry));
            }
            else {
                params.append(key, value);
            }
        });
        const stringified = params.toString();
        return stringified ? `?${stringified}` : '';
    }
    /**
     * @param {...string} args
     */
    static log(...args) {
        console.log(...args); // eslint-disable-line no-console
    }
    static request(url, opts) {
        let top;
        if (typeof window === 'object') {
            top = window;
        }
        return (top && typeof top.fetch === 'function')
            ? top.fetch(url, opts)
            : () => {
                throw new Error('"ActiveRecordSource" was not able to designate a default request library. You need to assign a request library to "ActiveRecordSource.request"');
            };
    }
}
ActiveRecordSource.Pagination = ActiveRecordPagination;
ActiveRecordSource.debug = false;
ActiveRecordSource.defaults = {
    headers: {},
};
export default ActiveRecordSource;
